import React, { useEffect, useState } from "react";
import { api } from "@/api";
import { Sprout, Gift, Check, Users } from "lucide-react";

const SUPPLIES = [
  "5-gallon buckets (est. 30–50 — ask hardware stores or bakeries for donations)",
  "Drill + bits for drainage holes (pre-drill before event day)",
  "Potting soil / compost, seedlings or seeds (kalo huli, kuʻula/greens, herbs)",
  "Hand trowels, gloves (keiki + adult sizes), watering can, care instruction cards",
  "Tarp for the soil station + broom for cleanup",
];

const inputCls = "w-full brutal-2 p-3 font-display focus:outline-none bg-white";

export default function Workshop() {
  const [info, setInfo] = useState(null);
  const [wishlist, setWishlist] = useState("");
  const [f, setF] = useState({ name: "", email: "", party_size: 1, phone: "", note: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const load = () => api.workshopInfo().then(setInfo).catch(() => {});
  useEffect(() => {
    load();
    api.config().then((c) => setWishlist(c.amazon_wishlist_url || "")).catch(() => {});
  }, []);

  const submit = async () => {
    setError("");
    if (!f.name.trim() || !f.email.includes("@")) { setError("Please enter your name and a valid email."); return; }
    setBusy(true);
    try {
      const res = await api.workshopSignup({ ...f, party_size: Number(f.party_size) || 1 });
      setDone(res);
      window.scrollTo(0, 0);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not reserve your garden. Please try again.");
      load();
    }
    setBusy(false);
  };

  if (done) {
    return (
      <div className="max-w-lg mx-auto p-4 pt-10 pb-28" data-testid="workshop-success">
        <div className="brutal p-6 text-center">
          <Sprout size={56} className="mx-auto text-green-600 mb-3" />
          <h1 className="font-display text-3xl font-black uppercase tracking-tight">You're planted in, {done.name}!</h1>
          <p className="font-display mt-2 text-gray-700">
            Your family's spot in the Bucket Garden Workshop is reserved for Pounders Fest 2026.
          </p>
          <div className="brutal-2 bg-[#FFD700] p-3 mt-4 text-left">
            <p className="font-display text-sm font-bold">🌱 One garden per family/household</p>
            <p className="font-display text-sm mt-1">Bring the whole ʻohana — you'll build & take home one bucket garden together. See you at the soil station!</p>
          </div>
          <button
            data-testid="workshop-another"
            onClick={() => { setDone(null); setF({ name: "", email: "", party_size: 1, phone: "", note: "" }); load(); }}
            className="mt-5 bg-black text-white font-display font-black uppercase tracking-widest px-6 py-3"
          >
            Reserve Another Family
          </button>
        </div>
      </div>
    );
  }

  const spots = info ? info.spots_left : null;
  const cap = info ? info.capacity : 0;
  const claimed = info ? info.gardens_claimed : 0;
  const pct = cap > 0 ? Math.min(100, Math.round((claimed / cap) * 100)) : 0;
  const full = info ? info.full : false;

  return (
    <div className="max-w-lg mx-auto p-4 pt-8 pb-28" data-testid="workshop-view">
      <div className="flex items-center gap-2 mb-1">
        <Sprout size={30} className="text-green-600" />
        <h1 className="font-display text-4xl font-black uppercase tracking-tight">Bucket Garden</h1>
      </div>
      <p className="font-display text-gray-600 text-sm mb-4">
        A free keiki + ʻohana workshop · Pounders Beach · September 2026
      </p>

      <div className="brutal p-4 mb-5">
        <p className="font-display text-sm text-gray-700">
          Build your own <strong>5-gallon bucket garden</strong> to take home — plant kalo, greens & herbs and grow kine right in your yard. One garden per family; bring the keiki to dig in!
        </p>
      </div>

      {/* Capacity */}
      {info && (
        <div className="brutal p-4 mb-5" data-testid="workshop-capacity-card">
          <div className="flex items-center justify-between mb-2">
            <span className="font-display font-black uppercase text-sm flex items-center gap-2">
              <Users size={16} /> Family spots
            </span>
            <span className={`font-score text-2xl ${full ? "text-[#E63946]" : "text-green-700"}`} data-testid="workshop-spots-left">
              {full ? "FULL" : `${spots} left`}
            </span>
          </div>
          <div className="brutal-2 h-4 w-full bg-white overflow-hidden">
            <div className="h-full bg-green-600 transition-[width] duration-500" style={{ width: `${pct}%` }} data-testid="workshop-progress" />
          </div>
          <p className="font-display text-[11px] text-gray-500 mt-1">{claimed} of {cap} gardens reserved</p>
        </div>
      )}

      {/* RSVP form */}
      {full ? (
        <div className="brutal-2 bg-[#FFF3BF] p-4 mb-5 text-center" data-testid="workshop-full">
          <p className="font-display font-bold uppercase">All gardens reserved 🌱</p>
          <p className="font-display text-sm mt-1">We're at capacity — check back in case a spot opens, or come cheer on the builders!</p>
        </div>
      ) : (
        <div className="brutal p-4 mb-5" data-testid="workshop-form">
          <h2 className="font-display text-xl font-black uppercase tracking-tight mb-1">Reserve Your Garden</h2>
          <p className="font-display text-xs text-gray-500 mb-3">One garden per family/household.</p>
          <div className="space-y-2">
            <input data-testid="workshop-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Family / your name" className={inputCls} />
            <input data-testid="workshop-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email" className={inputCls} />
            <div>
              <label className="font-display text-[10px] uppercase tracking-widest text-gray-500">How many coming (keiki + adults)?</label>
              <input data-testid="workshop-party" type="number" min="1" max="20" value={f.party_size} onChange={(e) => setF({ ...f, party_size: e.target.value })} className={`${inputCls} mt-1`} />
            </div>
            <input data-testid="workshop-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Phone (optional)" className={inputCls} />
            <textarea data-testid="workshop-note" rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Anything we should know? (optional)" className={inputCls} />
          </div>
          {error && <p className="text-[#E63946] font-bold uppercase text-sm mt-3" data-testid="workshop-error">{error}</p>}
          <button
            data-testid="workshop-submit"
            onClick={submit}
            disabled={busy}
            className="w-full mt-3 bg-black text-white font-display font-black uppercase tracking-widest py-4 active:scale-95 transition-transform disabled:opacity-60 flex items-center justify-center gap-2"
          >
            <Sprout size={18} /> {busy ? "Reserving…" : "Reserve Our Garden"}
          </button>
        </div>
      )}

      {/* Supply list / donations */}
      <div className="brutal p-4 mb-5" data-testid="workshop-supplies">
        <h2 className="font-display text-xl font-black uppercase tracking-tight mb-1">Help Us Grow It</h2>
        <p className="font-display text-xs text-gray-500 mb-3">
          Can you donate supplies? We'd love hardware-store or bakery bucket donations, soil, seedlings & tools. Every bit helps us plant more gardens.
        </p>
        <ul className="space-y-2 mb-4">
          {SUPPLIES.map((s, i) => (
            <li key={i} className="flex items-start gap-2 font-display text-sm" data-testid={`supply-${i}`}>
              <span className="brutal-2 w-5 h-5 shrink-0 mt-0.5 flex items-center justify-center bg-white">
                <Check size={13} className="text-green-600" />
              </span>
              <span className="text-gray-700">{s}</span>
            </li>
          ))}
        </ul>
        {wishlist && (
          <a
            data-testid="workshop-wishlist-link"
            href={wishlist}
            target="_blank"
            rel="noopener noreferrer"
            className="block brutal p-3 bg-[#FFD700] active:scale-[0.99] transition-transform"
          >
            <div className="flex items-center gap-2">
              <Gift size={20} className="shrink-0" />
              <span className="font-display font-black uppercase text-sm">Shop our Amazon wishlist 🎁</span>
            </div>
            <p className="font-display text-xs text-black/70 mt-1">Buy a needed item — it ships straight to us for the workshop. Mahalo!</p>
          </a>
        )}
      </div>
    </div>
  );
}
